import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../components/design/DataTable.jsx';
import {
  createImageScene,
  enqueueImageSceneGenerate,
  getImageSettings,
  listImageJobs,
  listImageScenes,
  mintPublicAssetLink,
  previewImage,
  retryImageJob,
  setSceneImageFavorite,
  updateImageScene,
  updateImageSettings,
  upscaleSceneImage
} from '../../services/contentAiService';

function safeJsonParse(text) {
  try {
    const v = JSON.parse(String(text || ''));
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

function prettyJson(v) {
  try {
    return JSON.stringify(v || {}, null, 2);
  } catch {
    return '{}';
  }
}

function clampNumber(v, { min, max, fallback }) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

const ACTIVE_IMAGE_PROVIDERS = ['dalle', 'gemini'];

function coerceProvider(value, fallback = 'dalle') {
  const normalized = String(value || '').trim().toLowerCase();
  return ACTIVE_IMAGE_PROVIDERS.includes(normalized) ? normalized : fallback;
}

export default function ImageAiPage() {
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({
    preferredProvider: 'dalle',
    fallbackEnabled: true,
    rateLimitRpm: 60,
    concurrencyLimit: 3,
    cacheEnabled: true,
    cacheTtlSeconds: 86400,
    openaiImageModel: 'dall-e-3',
    stableDiffusionBaseUrl: '',
    sdxlBaseUrl: '',
    fluxBaseUrl: ''
  });

  const [scenes, setScenes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsStatus, setJobsStatus] = useState('');

  const [sceneDialogOpen, setSceneDialogOpen] = useState(false);
  const [sceneEditingId, setSceneEditingId] = useState(null);
  const [sceneForm, setSceneForm] = useState({
    name: '',
    orderIndex: 0,
    prompt: '',
    negativePrompt: '',
    styleKey: 'documentary',
    styleJson: '{\n  "consistencyKey": "canal-1"\n}',
    width: 1024,
    height: 1024,
    batchSize: 4,
    seed: ''
  });

  const [sceneDetailsOpen, setSceneDetailsOpen] = useState(false);
  const [sceneDetails, setSceneDetails] = useState(null);
  const [assetUrlCache, setAssetUrlCache] = useState({});

  const [previewPrompt, setPreviewPrompt] = useState('Uma cena documental profissional sobre tecnologia, fotografia realista.');
  const [previewStyleKey, setPreviewStyleKey] = useState('documentary');
  const [previewConsistencyKey, setPreviewConsistencyKey] = useState('canal-1');
  const [previewWidth, setPreviewWidth] = useState(1024);
  const [previewHeight, setPreviewHeight] = useState(1024);
  const [previewBatchSize, setPreviewBatchSize] = useState(2);
  const [previewUpscaleEnabled, setPreviewUpscaleEnabled] = useState(false);
  const [previewUpscaleScale, setPreviewUpscaleScale] = useState(2);
  const [previewAssetIds, setPreviewAssetIds] = useState([]);

  const providers = [
    { key: 'dalle', label: 'DALL-E (OpenAI)' },
    { key: 'gemini', label: 'Gemini (Imagen)' }
  ];

  const loadSettings = useCallback(async () => {
    try {
      const resp = await getImageSettings();
      const data = resp?.data || null;
      setSettings(data);
      if (data) {
        setSettingsDraft((prev) => ({
          ...prev,
          preferredProvider: coerceProvider(data.preferredProvider, 'dalle'),
          fallbackEnabled: data.fallbackEnabled !== false,
          rateLimitRpm: Number(data.rateLimitRpm || 60),
          concurrencyLimit: Number(data.concurrencyLimit || 3),
          cacheEnabled: data.cacheEnabled !== false,
          cacheTtlSeconds: Number(data.cacheTtlSeconds || 86400),
          openaiImageModel: data.openaiImageModel || 'dall-e-3',
          stableDiffusionBaseUrl: data.stableDiffusionBaseUrl || '',
          sdxlBaseUrl: data.sdxlBaseUrl || '',
          fluxBaseUrl: data.fluxBaseUrl || ''
        }));
      }
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar configurações');
    }
  }, []);

  const loadScenes = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listImageScenes(200);
      const data = resp?.data || [];
      setScenes(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar cenas');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await listImageJobs({ limit: 200, status: jobsStatus || undefined });
      const data = resp?.data || [];
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar jobs');
    } finally {
      setLoading(false);
    }
  }, [jobsStatus]);

  useEffect(() => {
    loadSettings();
    loadScenes();
    loadJobs();
  }, [loadJobs, loadScenes, loadSettings]);

  const ensureAssetUrl = async (assetId) => {
    const key = String(assetId || '');
    if (!key) return null;
    if (assetUrlCache[key]) return assetUrlCache[key];
    const resp = await mintPublicAssetLink(assetId, 180);
    const url = resp?.data?.url || null;
    if (!url) return null;
    setAssetUrlCache((prev) => ({ ...prev, [key]: url }));
    return url;
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await updateImageSettings({
        preferredProvider: settingsDraft.preferredProvider,
        fallbackEnabled: settingsDraft.fallbackEnabled === true,
        rateLimitRpm: clampNumber(settingsDraft.rateLimitRpm, { min: 1, max: 3000, fallback: 60 }),
        concurrencyLimit: clampNumber(settingsDraft.concurrencyLimit, { min: 1, max: 100, fallback: 3 }),
        cacheEnabled: settingsDraft.cacheEnabled === true,
        cacheTtlSeconds: clampNumber(settingsDraft.cacheTtlSeconds, { min: 0, max: 604800, fallback: 86400 }),
        openaiImageModel: settingsDraft.openaiImageModel || null,
        stableDiffusionBaseUrl: settingsDraft.stableDiffusionBaseUrl || null,
        sdxlBaseUrl: settingsDraft.sdxlBaseUrl || null,
        fluxBaseUrl: settingsDraft.fluxBaseUrl || null
      });
      await loadSettings();
      toast.success('Configurações salvas');
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const openCreateScene = () => {
    setSceneEditingId(null);
    setSceneForm({
      name: '',
      orderIndex: scenes.length,
      prompt: '',
      negativePrompt: '',
      styleKey: 'documentary',
      styleJson: '{\n  "consistencyKey": "canal-1"\n}',
      width: 1024,
      height: 1024,
      batchSize: 4,
      seed: ''
    });
    setSceneDialogOpen(true);
  };

  const openEditScene = (row) => {
    setSceneEditingId(Number(row?.id || 0) || null);
    setSceneForm({
      name: row?.name || '',
      orderIndex: Number(row?.orderIndex || 0) || 0,
      prompt: row?.prompt || '',
      negativePrompt: row?.negativePrompt || '',
      styleKey: row?.styleKey || 'documentary',
      styleJson: row?.styleJson ? prettyJson(row.styleJson) : '{\n  "consistencyKey": "canal-1"\n}',
      width: Number(row?.width || 1024),
      height: Number(row?.height || 1024),
      batchSize: Number(row?.batchSize || 1),
      seed: row?.seed != null ? String(row.seed) : ''
    });
    setSceneDialogOpen(true);
  };

  const saveScene = async () => {
    const styleObj = safeJsonParse(sceneForm.styleJson);
    if (!sceneForm.name.trim()) {
      toast.error('Informe o nome');
      return;
    }
    if (!sceneForm.prompt.trim()) {
      toast.error('Informe o prompt');
      return;
    }
    if (!styleObj) {
      toast.error('styleJson inválido');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: sceneForm.name.trim(),
        orderIndex: Number(sceneForm.orderIndex || 0) || 0,
        prompt: sceneForm.prompt,
        negativePrompt: sceneForm.negativePrompt || null,
        styleKey: sceneForm.styleKey || null,
        styleJson: styleObj,
        width: clampNumber(sceneForm.width, { min: 256, max: 4096, fallback: 1024 }),
        height: clampNumber(sceneForm.height, { min: 256, max: 4096, fallback: 1024 }),
        batchSize: clampNumber(sceneForm.batchSize, { min: 1, max: 10, fallback: 1 }),
        seed: sceneForm.seed !== '' ? Number(sceneForm.seed) : null
      };
      if (sceneEditingId) {
        await updateImageScene(sceneEditingId, payload);
        toast.success('Cena atualizada');
      } else {
        await createImageScene(payload);
        toast.success('Cena criada');
      }
      setSceneDialogOpen(false);
      await loadScenes();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar cena');
    } finally {
      setLoading(false);
    }
  };

  const openSceneDetails = async (row) => {
    setSceneDetails(row);
    setSceneDetailsOpen(true);
  };

  const generateScene = async (row) => {
    const id = Number(row?.id || 0);
    if (!id) return;
    setLoading(true);
    try {
      await enqueueImageSceneGenerate(id, {});
      toast.success('Cena enfileirada');
      await loadJobs();
      await loadScenes();
    } catch (e) {
      toast.error(e?.message || 'Falha ao enfileirar');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (sceneId, sceneImageId, next) => {
    setLoading(true);
    try {
      await setSceneImageFavorite(sceneId, sceneImageId, next === true);
      await loadScenes();
    } catch (e) {
      toast.error(e?.message || 'Falha ao favoritar');
    } finally {
      setLoading(false);
    }
  };

  const runUpscale = async (sceneId, sceneImageId) => {
    setLoading(true);
    try {
      await upscaleSceneImage(sceneId, sceneImageId, 2);
      toast.success('Upscale concluído');
      await loadScenes();
    } catch (e) {
      toast.error(e?.message || 'Falha no upscale');
    } finally {
      setLoading(false);
    }
  };

  const runPreview = async () => {
    if (!previewPrompt.trim()) {
      toast.error('Informe o prompt');
      return;
    }
    setLoading(true);
    try {
      const resp = await previewImage({
        prompt: previewPrompt,
        styleKey: previewStyleKey || null,
        consistencyKey: previewConsistencyKey || null,
        width: Number(previewWidth) || 1024,
        height: Number(previewHeight) || 1024,
        batchSize: Number(previewBatchSize) || 1,
        allowCache: true,
        upscale: previewUpscaleEnabled ? { enabled: true, scale: Number(previewUpscaleScale) || 2 } : null
      });
      const assetIds = resp?.data?.assetIds || [];
      setPreviewAssetIds(Array.isArray(assetIds) ? assetIds : []);
      toast.success(resp?.data?.cacheHit ? 'Preview gerado (cache)' : 'Preview gerado');
    } catch (e) {
      toast.error(e?.message || 'Falha no preview');
    } finally {
      setLoading(false);
    }
  };

  const previewUrls = useMemo(() => {
    return previewAssetIds
      .map((id) => assetUrlCache[String(id)] || null)
      .filter(Boolean);
  }, [assetUrlCache, previewAssetIds]);

  const scenesColumns = [
    { key: 'orderIndex', label: 'Ordem' },
    { key: 'name', label: 'Cena' },
    { key: 'styleKey', label: 'Estilo' },
    { key: 'size', label: 'Resolução', render: (_, row) => `${row?.width || 0}x${row?.height || 0}` },
    { key: 'batchSize', label: 'Lote' },
    { key: 'status', label: 'Status' },
    { key: 'imagesCount', label: 'Imagens', render: (_, row) => (Array.isArray(row?.images) ? row.images.length : 0) },
    { key: 'favCount', label: 'Favoritas', render: (_, row) => (Array.isArray(row?.images) ? row.images.filter((x) => x?.isFavorite === true).length : 0) },
    { key: 'actions', label: 'Ações', render: (_, row) => (
      <Box display="flex" gap={1} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => openSceneDetails(row)}>Abrir</Button>
        <Button size="small" variant="outlined" onClick={() => openEditScene(row)}>Editar</Button>
        <Button size="small" variant="contained" onClick={() => generateScene(row)}>Gerar</Button>
      </Box>
    ) }
  ];

  const jobsColumns = [
    { key: 'id', label: 'ID' },
    { key: 'status', label: 'Status' },
    { key: 'scene', label: 'Cena', render: (_, row) => row?.scene?.name || row?.sceneId || '-' },
    { key: 'providerUsed', label: 'Provider' },
    { key: 'createdAt', label: 'Criado', render: (v) => (v ? new Date(v).toLocaleString() : '-') },
    { key: 'lastError', label: 'Erro', render: (v) => (v ? String(v).slice(0, 80) : '-') },
    { key: 'actions', label: 'Ações', render: (_, row) => (
      <Box display="flex" gap={1} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => retryImageJob(row.id).then(() => loadJobs()).catch((e) => toast.error(e?.message || 'Falha no retry'))}>Retry</Button>
      </Box>
    ) }
  ];

  const renderSceneImages = (row) => {
    const imgs = Array.isArray(row?.images) ? row.images : [];
    if (!imgs.length) return <Typography color="text.secondary">Sem imagens ainda</Typography>;
    return (
      <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}>
        {imgs.map((img) => (
          <Box key={img.id} display="grid" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={async () => {
                const url = await ensureAssetUrl(img.mediaAssetId);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              Abrir
            </Button>
            <Box
              sx={{
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                overflow: 'hidden',
                minHeight: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {assetUrlCache[String(img.mediaAssetId)] ? (
                <img alt="" src={assetUrlCache[String(img.mediaAssetId)]} style={{ width: '100%', display: 'block' }} />
              ) : (
                <Button size="small" onClick={() => ensureAssetUrl(img.mediaAssetId)}>Carregar preview</Button>
              )}
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                size="small"
                variant={img.isFavorite ? 'contained' : 'outlined'}
                onClick={() => toggleFavorite(row.id, img.id, !img.isFavorite)}
              >
                {img.isFavorite ? 'Favorita' : 'Favoritar'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => runUpscale(row.id, img.id)}>
                Upscale 2x
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box p={2} display="grid" gap={2}>
      <Typography variant="h5">Imagens IA</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Cenas" />
        <Tab label="Preview" />
        <Tab label="Jobs" />
        <Tab label="Configurações" />
      </Tabs>

      {tab === 0 ? (
        <Box display="grid" gap={2}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="contained" disabled={loading} onClick={openCreateScene}>Nova cena</Button>
            <Button variant="outlined" disabled={loading} onClick={loadScenes}>Recarregar</Button>
          </Box>
          <DataTable columns={scenesColumns} rows={scenes} getKey={(r) => r.id} />
        </Box>
      ) : null}

      {tab === 1 ? (
        <Box display="grid" gap={2} maxWidth={950}>
          <TextField
            label="Prompt"
            value={previewPrompt}
            onChange={(e) => setPreviewPrompt(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}>
            <TextField label="Estilo" value={previewStyleKey} onChange={(e) => setPreviewStyleKey(e.target.value)} fullWidth />
            <TextField label="Consistency key" value={previewConsistencyKey} onChange={(e) => setPreviewConsistencyKey(e.target.value)} fullWidth />
            <TextField label="Qtd variações" type="number" value={previewBatchSize} onChange={(e) => setPreviewBatchSize(e.target.value)} inputProps={{ min: 1, max: 10 }} fullWidth />
            <TextField label="Width" type="number" value={previewWidth} onChange={(e) => setPreviewWidth(e.target.value)} inputProps={{ min: 256, max: 2048 }} fullWidth />
            <TextField label="Height" type="number" value={previewHeight} onChange={(e) => setPreviewHeight(e.target.value)} inputProps={{ min: 256, max: 2048 }} fullWidth />
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <Switch checked={previewUpscaleEnabled === true} onChange={(e) => setPreviewUpscaleEnabled(e.target.checked)} />
                <Typography>Upscale</Typography>
              </Box>
              <TextField
                label="Scale"
                type="number"
                value={previewUpscaleScale}
                onChange={(e) => setPreviewUpscaleScale(e.target.value)}
                disabled={!previewUpscaleEnabled}
                inputProps={{ min: 1, max: 4, step: 1 }}
                sx={{ width: 120 }}
              />
            </Box>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="contained" disabled={loading} onClick={runPreview}>Gerar preview</Button>
            <Button
              variant="outlined"
              disabled={loading}
              onClick={async () => {
                setPreviewAssetIds([]);
                setAssetUrlCache({});
              }}
            >
              Limpar
            </Button>
          </Box>
          {previewAssetIds.length ? (
            <Box display="grid" gap={2}>
              <Typography variant="subtitle1">Resultados</Typography>
              <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}>
                {previewAssetIds.map((assetId) => (
                  <Box key={assetId} display="grid" gap={1}>
                    <Button size="small" variant="outlined" onClick={() => ensureAssetUrl(assetId)}>Carregar</Button>
                    {assetUrlCache[String(assetId)] ? (
                      <img alt="" src={assetUrlCache[String(assetId)]} style={{ width: '100%', borderRadius: 6 }} />
                    ) : (
                      <Box sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, minHeight: 120 }} />
                    )}
                  </Box>
                ))}
              </Box>
              {previewUrls.length ? (
                <Typography variant="body2" color="text.secondary">
                  {previewUrls.length} previews carregados
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}

      {tab === 2 ? (
        <Box display="grid" gap={2}>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={jobsStatus} onChange={(e) => setJobsStatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="queued">queued</MenuItem>
                <MenuItem value="running">running</MenuItem>
                <MenuItem value="retrying">retrying</MenuItem>
                <MenuItem value="failed">failed</MenuItem>
                <MenuItem value="completed">completed</MenuItem>
              </Select>
            </FormControl>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <Button variant="outlined" disabled={loading} onClick={loadJobs}>Atualizar</Button>
              <Button variant="outlined" disabled={loading} onClick={loadScenes}>Atualizar cenas</Button>
            </Box>
          </Box>
          <DataTable columns={jobsColumns} rows={jobs} getKey={(r) => r.id} />
        </Box>
      ) : null}

      {tab === 3 ? (
        <Box display="grid" gap={2} maxWidth={950}>
          <Typography variant="h6">Providers</Typography>
          <FormControl fullWidth>
            <InputLabel>Provider preferido</InputLabel>
            <Select
              label="Provider preferido"
              value={settingsDraft.preferredProvider}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, preferredProvider: e.target.value }))}
            >
              {providers.map((p) => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={settingsDraft.fallbackEnabled === true} onChange={(e) => setSettingsDraft((p) => ({ ...p, fallbackEnabled: e.target.checked }))} />
              <Typography>Fallback providers</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Switch checked={settingsDraft.cacheEnabled === true} onChange={(e) => setSettingsDraft((p) => ({ ...p, cacheEnabled: e.target.checked }))} />
              <Typography>Cache</Typography>
            </Box>
          </Box>
          <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField
              label="Rate limit (RPM)"
              type="number"
              value={settingsDraft.rateLimitRpm}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, rateLimitRpm: e.target.value }))}
              inputProps={{ min: 1, max: 3000 }}
              fullWidth
            />
            <TextField
              label="Concurrency"
              type="number"
              value={settingsDraft.concurrencyLimit}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, concurrencyLimit: e.target.value }))}
              inputProps={{ min: 1, max: 100 }}
              fullWidth
            />
            <TextField
              label="Cache TTL (segundos)"
              type="number"
              value={settingsDraft.cacheTtlSeconds}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, cacheTtlSeconds: e.target.value }))}
              inputProps={{ min: 0, max: 604800 }}
              fullWidth
            />
            <TextField
              label="OpenAI image model"
              value={settingsDraft.openaiImageModel}
              onChange={(e) => setSettingsDraft((p) => ({ ...p, openaiImageModel: e.target.value }))}
              fullWidth
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Providers locais de imagem foram desativados nesta fase de limpeza e não são mais expostos nesta tela.
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button variant="outlined" disabled={loading} onClick={loadSettings}>Recarregar</Button>
            <Button variant="contained" disabled={loading} onClick={saveSettings}>Salvar</Button>
          </Box>
          {settings?.updatedAt ? (
            <Typography variant="body2" color="text.secondary">Atualizado em: {new Date(settings.updatedAt).toLocaleString()}</Typography>
          ) : null}
        </Box>
      ) : null}

      <Dialog open={sceneDialogOpen} onClose={() => setSceneDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{sceneEditingId ? `Editar cena #${sceneEditingId}` : 'Nova cena'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
            <TextField label="Nome" value={sceneForm.name} onChange={(e) => setSceneForm((p) => ({ ...p, name: e.target.value }))} fullWidth />
            <TextField label="Ordem" type="number" value={sceneForm.orderIndex} onChange={(e) => setSceneForm((p) => ({ ...p, orderIndex: e.target.value }))} fullWidth />
            <TextField label="Width" type="number" value={sceneForm.width} onChange={(e) => setSceneForm((p) => ({ ...p, width: e.target.value }))} inputProps={{ min: 256, max: 4096 }} fullWidth />
            <TextField label="Height" type="number" value={sceneForm.height} onChange={(e) => setSceneForm((p) => ({ ...p, height: e.target.value }))} inputProps={{ min: 256, max: 4096 }} fullWidth />
            <TextField label="Lote (variações)" type="number" value={sceneForm.batchSize} onChange={(e) => setSceneForm((p) => ({ ...p, batchSize: e.target.value }))} inputProps={{ min: 1, max: 10 }} fullWidth />
            <TextField label="Seed (opcional)" value={sceneForm.seed} onChange={(e) => setSceneForm((p) => ({ ...p, seed: e.target.value }))} fullWidth />
            <TextField label="Estilo" value={sceneForm.styleKey} onChange={(e) => setSceneForm((p) => ({ ...p, styleKey: e.target.value }))} fullWidth />
          </Box>
          <Box mt={2} display="grid" gap={2}>
            <TextField label="Prompt" value={sceneForm.prompt} onChange={(e) => setSceneForm((p) => ({ ...p, prompt: e.target.value }))} fullWidth multiline minRows={4} />
            <TextField label="Negative prompt (opcional)" value={sceneForm.negativePrompt} onChange={(e) => setSceneForm((p) => ({ ...p, negativePrompt: e.target.value }))} fullWidth multiline minRows={2} />
            <TextField label="styleJson" value={sceneForm.styleJson} onChange={(e) => setSceneForm((p) => ({ ...p, styleJson: e.target.value }))} fullWidth multiline minRows={4} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSceneDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={loading} onClick={saveScene}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sceneDetailsOpen} onClose={() => setSceneDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{sceneDetails?.name ? `Cena: ${sceneDetails.name}` : 'Cena'}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button variant="outlined" disabled={loading} onClick={() => sceneDetails && generateScene(sceneDetails)}>Gerar novamente</Button>
              <Button
                variant="outlined"
                disabled={loading}
                onClick={async () => {
                  await loadScenes();
                  const refreshed = scenes.find((s) => String(s.id) === String(sceneDetails?.id));
                  setSceneDetails(refreshed || sceneDetails);
                }}
              >
                Atualizar
              </Button>
            </Box>
            {renderSceneImages(sceneDetails)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSceneDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
